import {
  Flag,
  MoreVertical,
  PauseCircle,
  PencilLine,
  PlayCircle,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Menu, type MenuItem } from '@/components/ui/Menu';
import { cn } from '@/lib/utils';
import type { House } from '@/types/house';

/** "42 students", with the Indian digit grouping every other count uses. */
export function studentsLabel(count: number): string {
  return `${count.toLocaleString('en-IN')} ${count === 1 ? 'student' : 'students'}`;
}

interface HouseCardProps {
  house: House;
  isSelected: boolean;
  canManage: boolean;
  onView: () => void;
  onAssign: () => void;
  onEdit: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  /** Suspended houses only — an active one must be suspended first. */
  onDelete: () => void;
}

/**
 * One house on the Houses page — active or suspended, the same card.
 *
 * An active house offers Suspend and nothing stronger. A suspended one says so,
 * offers Reactivate and Delete permanently instead, and has no Assign button.
 * Deleting is kept to the suspended list so it is always the second of two
 * deliberate steps.
 */
export function HouseCard({
  house,
  isSelected,
  canManage,
  onView,
  onAssign,
  onEdit,
  onSuspend,
  onReactivate,
  onDelete,
}: HouseCardProps) {
  const isSuspended = house.status === 'SUSPENDED';

  const actions: MenuItem[] = [
    {
      key: 'edit',
      label: 'Edit house',
      icon: <PencilLine className="h-4 w-4" aria-hidden />,
      onSelect: onEdit,
    },
    ...(isSuspended
      ? [
          {
            key: 'reactivate',
            label: 'Reactivate house',
            description: 'Students can be placed in it again.',
            icon: <PlayCircle className="h-4 w-4" aria-hidden />,
            onSelect: onReactivate,
          },
          {
            key: 'delete',
            label: 'Remove the house for good',
            description: 'This action will permanently remove the house and its data.',
            icon: <Trash2 className="h-4 w-4" aria-hidden />,
            tone: 'danger' as const,
            onSelect: onDelete,
          } satisfies MenuItem,
        ]
      : [
          {
            key: 'suspend',
            label: 'Suspend house',
            description: 'Only a house with no students can be suspended.',
            icon: <PauseCircle className="h-4 w-4" aria-hidden />,
            tone: 'danger' as const,
            onSelect: onSuspend,
          },
        ]),
  ];

  return (
    <li
      className={cn(
        'card flex flex-col justify-between gap-3 p-3.5 sm:p-4 rounded-xl transition-all duration-200 shadow-2xs hover:shadow-xs',
        isSelected
          ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-500/20 bg-indigo-500/[0.02]'
          : 'border-line hover:border-indigo-400/40',
        isSuspended && 'bg-surface-2 opacity-85',
      )}
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="flex items-start gap-2.5 min-w-0">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200/70 dark:border-indigo-800/70 shadow-2xs mt-0.5">
            <Flag className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="truncate text-base font-bold text-ink tracking-tight" title={house.name}>
                {house.name}
              </h2>
              {isSuspended && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-pill bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  Suspended
                </span>
              )}
            </div>
            {house.description && (
              <p className="mt-0.5 text-xs text-ink-3 line-clamp-1" title={house.description}>
                {house.description}
              </p>
            )}
          </div>
        </div>
        {canManage && (
          <Menu
            label={`More actions for ${house.name}`}
            trigger={<MoreVertical className="h-4 w-4" aria-hidden />}
            triggerClassName="h-7 w-7 text-ink-3 hover:text-ink hover:bg-surface-2 rounded-lg -mr-1"
            items={actions}
          />
        )}
      </div>

      <div className="mt-1 flex items-center justify-between gap-2 border-t border-line/70 pt-2.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-2">
          <Users className="h-3.5 w-3.5 text-ink-3" aria-hidden />
          {studentsLabel(house.studentCount)}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {canManage && !isSuspended && (
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<UserPlus className="h-3 w-3" />}
              onClick={onAssign}
              className="!h-7 !px-2.5 !text-sm font-semibold"
            >
              Assign
            </Button>
          )}
          <Button
            size="sm"
            variant={isSelected ? 'primary' : 'secondary'}
            aria-pressed={isSelected}
            onClick={onView}
            className="!h-7 !px-2.5 !text-sm font-semibold"
          >
            View
          </Button>
        </div>
      </div>
    </li>
  );
}
